// Insert apostrophe thousands separators into a digit string (linear scan; avoids
// the super-linear backtracking of /\B(?=(\d{3})+(?!\d))/).
const groupThousands = (intStr: string): string => {
  let out = '';
  for (let i = 0; i < intStr.length; i++) {
    if (i > 0 && (intStr.length - i) % 3 === 0) out += "'";
    out += intStr[i];
  }
  return out;
};

const formatWithApostrophes = (value: number, decimals?: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  const fixed = decimals === undefined ? String(Math.round(abs)) : abs.toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const formattedInt = groupThousands(intPart);
  return decPart === undefined ? `${sign}${formattedInt}` : `${sign}${formattedInt}.${decPart}`;
};

/**
 * Formats a number using apostrophes as thousand separators.
 * If the absolute value has 7 or more digits (>= 1,000,000), it is displayed
 * in millions, e.g. 743416614 → "743.41M".
 *
 * @param value    The number to format
 * @param decimals Optional fixed decimal places (omit for integer rounding)
 */
export const formatNumber = (value: number, decimals?: number): string => {
  const abs = Math.abs(value);

  // Show in millions for values with 7+ digits
  if (abs >= 1_000_000) {
    const millions = abs / 1_000_000;
    const formatted = millions.toFixed(2);
    const [intPart, decPart] = formatted.split('.');
    const formattedInt = groupThousands(intPart);
    return `${value < 0 ? '-' : ''}${formattedInt}.${decPart}M`;
  }

  return formatWithApostrophes(value, decimals);
};

/**
 * Formats chart values without compacting large numbers.
 * This keeps axis labels and tooltips readable on line charts.
 */
export const formatChartNumber = (value: number, decimals?: number): string => {
  return formatWithApostrophes(value, decimals);
};

/**
 * Converts commas in a string value to apostrophes (thousand separators).
 * e.g. "743,416,614" → "743'416'614"
 * Also applies millions formatting for numeric strings with 7+ digits.
 */
export const formatStringValue = (value: string | number): string | number => {
  if (typeof value === 'number') {
    return formatNumber(value);
  }
  if (typeof value !== 'string') return value;

  // Check if the string is a pure number with commas (e.g. "743,416,614" or "1,234.56")
  const stripped = value.replaceAll(',', '');
  const num = Number(stripped);
  if (!Number.isNaN(num) && stripped.length > 0 && /^-?[\d,]+(\.\d+)?$/.test(value)) {
    return formatNumber(num);
  }

  // For non-numeric strings, just replace commas with apostrophes
  return value.replaceAll(',', "'");
};

/**
 * "% of Rejects" = errored shipments as a share of total transactions.
 * Inputs may be numbers or numeric strings (date-range SUMs come back as strings from PG).
 */
export const formatRejectRate = (errored: number | string, total: number | string): string => {
  const t = Number(total) || 0;
  if (t <= 0) return '0%';
  const pct = ((Number(errored) || 0) / t) * 100;
  return `${pct.toFixed(2)}%`;
};
